import React, { useState, useRef, useCallback } from 'react';

export default function CommentDiv({ 
  comment, 
  zoom, 
  onCommentClick,
  onCommentMove,
  isActive 
}) {
  const [layerSize, setLayerSize] = useState({ width: 0, height: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const divRef = useRef(null);

  React.useEffect(() => {
    const updateLayerSize = () => {
      const textLayer = divRef.current?.parentElement;
      if (!textLayer) return;
      const r = textLayer.getBoundingClientRect();
      setLayerSize({ width: r.width, height: r.height });
    };
    
    updateLayerSize();
    window.addEventListener("resize", updateLayerSize);
    
    const timeoutId = setTimeout(updateLayerSize, 100);
    
    return () => {
      window.removeEventListener("resize", updateLayerSize);
      clearTimeout(timeoutId);
    };
  }, [zoom]);

  // Convert normalized to pixel coordinates
  const pixelTop = comment.normalizedTop * layerSize.height;
  const pixelLeft = comment.normalizedLeft * layerSize.width;

  // Mouse move handler - update position during drag
  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !layerSize.width || !layerSize.height) return;

    console.log('Mouse move - dragging:', isDragging);
    console.log('onCommentMove type:', typeof onCommentMove);

    // Safety check - make sure onCommentMove is a function
    if (typeof onCommentMove !== 'function') {
      console.error('onCommentMove is not a function:', onCommentMove);
      setIsDragging(false);
      document.body.style.cursor = '';
      return;
    }

    const textLayer = divRef.current?.parentElement;
    if (!textLayer) return;

    const layerRect = textLayer.getBoundingClientRect();
    
    // Calculate new position based on mouse position and offset
    const newPixelLeft = e.clientX - layerRect.left - dragOffset.x;
    const newPixelTop = e.clientY - layerRect.top - dragOffset.y;
    
    // Convert to normalized coordinates (0-1 range)
    const normalizedLeft = Math.max(0, Math.min(1, newPixelLeft / layerSize.width));
    const normalizedTop = Math.max(0, Math.min(1, newPixelTop / layerSize.height));
    
    console.log('Calling onCommentMove with:', { 
      commentId: comment.id, 
      normalizedLeft, 
      normalizedTop 
    });
    
    // Update comment position
    onCommentMove(comment.id, normalizedLeft, normalizedTop);
  }, [isDragging, layerSize, dragOffset, comment.id, onCommentMove]);

  // Mouse up handler - end dragging
  const handleMouseUp = useCallback(() => {
    console.log('Mouse up - ending drag');
    if (isDragging) {
      setIsDragging(false);
      document.body.style.cursor = '';
    }
  }, [isDragging]);

  // Add/remove global mouse event listeners for dragging
  React.useEffect(() => {
    if (isDragging) {
      console.log('Adding mouse event listeners');
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      console.log('Removing mouse event listeners');
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Mouse down handler - start dragging
  const handleMouseDown = (e) => {
    e.stopPropagation();
    
    console.log('Mouse down - starting drag');
    console.log('onCommentMove available:', typeof onCommentMove === 'function');
    
    // Check if dragging is supported
    if (typeof onCommentMove !== 'function') {
      console.error('Dragging not supported - onCommentMove is not a function');
      return;
    }
    
    setIsDragging(true);
    
    // Calculate offset from mouse to comment center
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    setDragOffset({ x: offsetX, y: offsetY });
    
    // Change cursor during drag
    document.body.style.cursor = 'grabbing';
  };

  // Click handler (for non-drag clicks)
  const handleClick = (e) => {
    // Only trigger click if not dragging (quick click vs drag)
    if (!isDragging) {
      e.stopPropagation();
      if (typeof onCommentClick === 'function') {
        onCommentClick(comment.id);
      }
    }
  };

  // Check if dragging is supported
  const isDraggable = typeof onCommentMove === 'function';

  console.log('CommentDiv render - draggable:', isDraggable, 'comment:', comment.id);

  return (
    <div
      ref={divRef}
      className="comment-marker"
      onClick={handleClick}
      onMouseDown={isDraggable ? handleMouseDown : undefined}
      style={{
        position: "absolute",
        top: `${pixelTop}px`,
        left: `${pixelLeft}px`,
        width: '20px',
        height: '20px',
        background: isActive ? '#ff6b6b' : (isDragging ? '#0056b3' : '#007bff'),
        borderRadius: '50%',
        cursor: isDraggable ? (isDragging ? 'grabbing' : 'grab') : 'pointer',
        border: '2px solid white',
        boxShadow: isDragging ? '0 4px 8px rgba(0,0,0,0.4)' : '0 2px 4px rgba(0,0,0,0.3)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        color: 'white',
        fontWeight: 'bold',
        zIndex: isDragging ? 30 : 20,
        transform: isDragging ? 'scale(1.1)' : 'scale(1)',
        transition: isDragging ? 'none' : 'transform 0.1s ease',
        pointerEvents: 'auto',
        userSelect: 'none'
      }}
      title={comment.text || `Comment${isDraggable ? " (drag to move)" : ""}`}
    >
      💬
    </div>
  );
}